import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/SimpleToast";
import { AlertTriangle, RefreshCw, Trash2, Loader2 } from "lucide-react";

const API_URL = "/api";

export default function ErrorLog() {
  const toast = useToast();
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/errors/recent?limit=100`);
      setErrors(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const clearAll = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Alle Fehler wirklich löschen?")) return;
    try {
      const r = await axios.delete(`${API_URL}/errors/clear`);
      toast.success(`${r.data.deleted} Fehler gelöscht`);
      fetchErrors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  return (
    <div className="space-y-6" data-testid="error-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-500" />
            Fehler-Protokoll
          </h1>
          <p className="text-muted-foreground">Frontend-Fehler aller Benutzer (Production-Debugging)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchErrors} disabled={loading} data-testid="refresh-errors-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
          <Button variant="destructive" onClick={clearAll} data-testid="clear-errors-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Alle löschen
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : errors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Keine Fehler protokolliert</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {errors.map((err) => {
            const isExpanded = expanded === err.error_id;
            return (
              <Card key={err.error_id} className="overflow-hidden" data-testid={`error-card-${err.error_id}`}>
                <CardHeader className="cursor-pointer pb-3" onClick={() => setExpanded(isExpanded ? null : err.error_id)}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Badge variant={err.type === "unhandled_promise_rejection" ? "destructive" : "secondary"}>
                          {err.type || "error"}
                        </Badge>
                        <span className="truncate">{err.message}</span>
                      </CardTitle>
                      <CardDescription className="mt-1 text-xs font-mono">
                        {err.error_id} · {new Date(err.server_timestamp || err.timestamp).toLocaleString("de-DE")} · v{err.app_version}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="space-y-3 text-sm border-t bg-slate-50 dark:bg-slate-900/30">
                    <div>
                      <div className="font-semibold text-xs text-muted-foreground mb-1">URL</div>
                      <code className="block p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs break-all">{err.url || "-"}</code>
                    </div>
                    <div>
                      <div className="font-semibold text-xs text-muted-foreground mb-1">User-Agent</div>
                      <code className="block p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs break-all">{err.user_agent || "-"}</code>
                    </div>
                    {err.stack && (
                      <div>
                        <div className="font-semibold text-xs text-muted-foreground mb-1">Stack Trace</div>
                        <pre className="p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">{err.stack}</pre>
                      </div>
                    )}
                    {err.component_stack && (
                      <div>
                        <div className="font-semibold text-xs text-muted-foreground mb-1">React Component Stack</div>
                        <pre className="p-2 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">{err.component_stack}</pre>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
